/**
 * Service for handling order submission with proper type conversion
 * Bridges the gap between UI types and Domain types using the adapter
 */

import { OrderAdapter } from '../adapters/OrderAdapter';
import type { IOrderRepository } from '../../domain/repositories/IOrderRepository';
import type { IProductRepository } from '../../domain/repositories/IProductRepository';
import type { OrderReadyToSubmit } from '@/components/order/order.types';

export interface OrderSubmissionResult {
  orderId: string;
  timestamp: number;
}

export class OrderSubmissionService {
  private orderAdapter: OrderAdapter;

  constructor(
    private orderRepository: IOrderRepository,
    productRepository: IProductRepository,
  ) {
    this.orderAdapter = new OrderAdapter(productRepository);
  }

  /**
   * Submit order with proper type conversion from UI to Domain layer
   */
  async submitOrder(orderData: OrderReadyToSubmit): Promise<OrderSubmissionResult> {
    try {
      // Convert UI format to Domain format using adapter
      const createOrderDto = await this.orderAdapter.toCreateOrderDto(orderData);

      // Submit using domain repository
      const result = await this.orderRepository.submitOrder(createOrderDto);

      return result;
    } catch (error) {
      console.error('OrderSubmissionService: Failed to submit order:', error);
      throw error;
    }
  }

  /**
   * Validate order data before submission
   */
  async validateOrder(orderData: OrderReadyToSubmit): Promise<{ isValid: boolean; errors: Record<string, string> }> {
    try {
      // Use the adapter to validate that all required data can be resolved
      await this.orderAdapter.toCreateOrderDto(orderData);

      // If we get here, all products/variants were found successfully
      return { isValid: true, errors: {} };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Validation failed';
      return {
        isValid: false,
        errors: {
          general: errorMessage,
        },
      };
    }
  }
}
